/* eslint-disable camelcase */
import React from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';

import {
  Alert,
  Table,
  OverlayTrigger,
  Glyphicon,
  Tooltip,
  Row,
  Col,
  Button,
} from 'react-bootstrap';
import moment from 'moment';

import Overlay from '../../../components/Overlay';
import { getUserTimesheetLink } from '../../timesheetNavigator/links';
import NewTabLink from '../../../components/NewTabLink';
import EditTicketNumberDetailsModal, {
  editTicketNumberDetailsModalPropTypes,
  withEditTicketNumberDetailsModalState,
} from './EditTicketNumberDetailsModal';

const propTypes = exact({
  // eslint-disable-next-line react/no-unused-prop-types
  ticketNumberId: PropTypes.number.isRequired,
  ticketNumberObject: PropTypes.object.isRequired,
  ticketNumberData: PropTypes.object.isRequired,

  ...editTicketNumberDetailsModalPropTypes,
});

const tooltip = (notes) => {
  return (
    <Tooltip id="tooltip" placement="top">
      {notes}
      {/* <strong>This ticket can not be changed.</strong> Ticket has already been processed. */}
    </Tooltip>
  );
};

const TicketNumberSummary = (props) => {
  const renderNewTabTime = (day, userId) => {
    const { timeEntries } = day;
    const paidPerDiem = (timeEntries.length > 0) ? timeEntries[0].paidPerDiem : false;

    const url = getUserTimesheetLink(userId, moment(day.date.iso8601));
    return (
      <div>
        <NewTabLink style={{ display: 'inline' }} to={url}>{day.worked_hours.to_string}</NewTabLink>
        {paidPerDiem && (
          <OverlayTrigger placement="top" overlay={tooltip('Per Diem Paid')}>
            <Glyphicon glyph="usd" />
          </OverlayTrigger>
        )}
      </div>
    );
  };

  const renderRow = (edge) => {
    const user_hours = edge.node;

    const { id, firstname, lastname } = user_hours.user;
    const { user_worked_hours_total, daily_time_entries } = user_hours;
    return (
      <tr key={id}>
        <td>{firstname} {lastname}</td>
        {daily_time_entries.map((day) => {
          return (
            <td key={day.date.iso8601}>
              {renderNewTabTime(day, id)}
            </td>
          );
        })}
        <td>
          <span style={{ fontWeight: 'bold' }}>{user_worked_hours_total.to_string}</span>
        </td>
      </tr>
    );
  };

  const renderTotals = (inputData) => {
    const { time_entry_group } = inputData;
    const { daily_time_entries, total_hours } = time_entry_group;
    return (
      <tr>
        <td style={{ fontWeight: 'bold' }}>Daily Totals</td>
        {daily_time_entries.map((dayObject) => {
          const { display } = dayObject.date;
          return (
            <td style={{ fontWeight: 'bold' }} key={display}>
              {dayObject.total_hours.to_string}
            </td>
          );
        })}
        <td style={{ fontWeight: 'bold' }}>{total_hours.to_string}</td>
      </tr>
    );
  };

  const renderTimeEntryTable = () => {
    const { user_daily_time_entry_groups } = props.ticketNumberData;

    const { edges } = user_daily_time_entry_groups;
    const { interval } = user_daily_time_entry_groups.time_entry_group;

    const tableHeaders = [
      'Name',
      ...interval.map((day) => { return `${day.day_of_week} ${day.month}-${day.day}`; }),
      'Total Hours',
    ];

    return (
      <Table>
        <thead>
          <tr>
            {tableHeaders.map((header) => { return <th key={header}>{header}</th>; })}
          </tr>
        </thead>
        <tbody>
          {edges.map(renderRow, this)}
        </tbody>
        <tfoot>
          {renderTotals(user_daily_time_entry_groups)}
        </tfoot>
      </Table>
    );
  };

  const renderTicketFacts = () => {
    const {
      district,
      department,
      number,
      jobNumber,
      operator,
      lease,
      id,
    } = props.ticketNumberData;

    const { showModal, openModal, closeModal } = props;

    const jobNumberNumber = (jobNumber) ? jobNumber.number : '-';
    const districtName = (district) ? district.name : '-';
    const departmentName = (department) ? department.name : '-';
    const operatorName = (operator) ? operator.name : '-';
    const leaseName = (lease) ? lease.name : '-';
    return (
      <div>
        <Row>
          <Col xs={3}>
            <Table condensed>
              <tbody>
                <tr>
                  <td className="text-left"><strong>Ticket Number: </strong>{number}</td>
                </tr>
                <tr>
                  <td className="text-left"><strong>Job Number: </strong>{jobNumberNumber}</td>
                </tr>
              </tbody>
            </Table>
          </Col>
          <Col xs={3}>
            <Table condensed>
              <tbody>
                <tr>
                  <td className="text-left"><strong>Operator: </strong>{operatorName}</td>
                </tr>
                <tr>
                  <td className="text-left"><strong>Lease: </strong>{leaseName}</td>
                </tr>
              </tbody>
            </Table>
          </Col>
          <Col xs={3}>
            <Table condensed>
              <tbody>
                <tr>
                  <td className="text-left"><strong>District: </strong>{districtName}</td>
                </tr>
                <tr>
                  <td className="text-left"><strong>Department: </strong>{departmentName}</td>
                </tr>
              </tbody>
            </Table>
          </Col>
          <Col xs={3}>
            <div style={{ marginTop: '30px' }} className="inline-btn-styling">
              <Overlay enabled={!jobNumber} message="Only supported for ticket numbers generated using the app.">
                <div className="tooltip-area">
                  <Button
                    disabled={!jobNumber}
                    bsStyle="warning"
                    onClick={openModal}
                  >
                    Edit Details
                  </Button>
                </div>
              </Overlay>
            </div>
          </Col>
        </Row>
        <EditTicketNumberDetailsModal
          formData={{
            job_number: jobNumber,
            district,
            department,
            operator: (operator) ? operator.lookup : null,
            lease: (lease) ? lease.lookup : null,
            ticket_number: { id, number },
          }}
          show={showModal}
          onHide={closeModal}
        />
      </div>
    );
  };

  const renderMissingTimeEntries = () => {
    const { ticketNumberObject } = props;
    return (
      <Alert bsStyle="warning">
        <h4>No time entries found for ticket number {ticketNumberObject.number} </h4>
      </Alert>
    );
  };

  const { user_daily_time_entry_groups } = props.ticketNumberData;
  const hasTimeEntries = !!user_daily_time_entry_groups;

  return (
    <div>
      {renderTicketFacts()}
      {!hasTimeEntries && renderMissingTimeEntries()}
      {hasTimeEntries && renderTimeEntryTable()}
    </div>
  );
};

TicketNumberSummary.propTypes = propTypes;

export default withEditTicketNumberDetailsModalState(TicketNumberSummary);
